import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useSpaces } from '@/client/modules/discussions/hooks/useSpaces'
import { MessageSquare, ChevronRight, Hash } from 'lucide-react'
import { formatRelativeTime, getInitials } from '@/lib/utils'

/**
 * DiscussionsWidget Component
 *
 * Displays recent discussion spaces and activity on the dashboard.
 * Shows:
 * - Active spaces with recent activity
 * - Message counts
 * - Space creators
 */
export function DiscussionsWidget() {
  const { data: spaces, isLoading, error } = useSpaces()

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">Discussions</CardTitle>
            </div>
          </div>
          <CardDescription>Team conversations and threads</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Discussions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load discussions</p>
        </CardContent>
      </Card>
    )
  }

  const spacesList = spaces || []

  // Sort by last activity (most recent first)
  const sortedSpaces = [...spacesList].sort((a, b) => {
    const aTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0
    const bTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0
    return bTime - aTime
  })

  // Take top 4 spaces
  const recentSpaces = sortedSpaces.slice(0, 4)

  // Calculate totals
  const totalSpaces = spacesList.length
  const totalMessages = spacesList.reduce((sum, s) => sum + (s.messageCount || 0), 0)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Discussions</CardTitle>
          </div>
          <Link
            to="/dashboard/discussions"
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            View all
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <CardDescription>Team conversations and threads</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Stats summary */}
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                <span className="font-bold">{totalSpaces}</span>{' '}
                <span className="text-muted-foreground">
                  {totalSpaces === 1 ? 'space' : 'spaces'}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                <span className="font-bold">{totalMessages}</span>{' '}
                <span className="text-muted-foreground">
                  {totalMessages === 1 ? 'message' : 'messages'}
                </span>
              </span>
            </div>
          </div>

          {/* Recent spaces list */}
          {recentSpaces.length > 0 ? (
            <div className="space-y-2 pt-2 border-t">
              {recentSpaces.map((space) => (
                <Link
                  key={space.id}
                  to={`/dashboard/discussions/${space.slug}`}
                  className="flex items-center justify-between p-2 -mx-2 rounded-md hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {getInitials(space.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                        {space.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {space.messageCount || 0} messages
                        {space.lastActivityAt && (
                          <span>
                            {' '}
                            · {formatRelativeTime(space.lastActivityAt)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 border-t">
              <p className="text-sm text-muted-foreground">No discussion spaces yet</p>
              <Link
                to="/dashboard/discussions"
                className="text-xs text-primary hover:underline mt-1 inline-block"
              >
                Create your first space
              </Link>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
